import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { MapPin, Clock, Phone, Navigation, MessageCircle } from "lucide-react";
import { whatsappLink, ADDRESS, PHONE_DISPLAY } from "../../lib/api";

const STOREFRONT_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_5259e3a7-5fd5-4368-ad3c-7bc346c4dca8/artifacts/kbti59mz_WhatsApp%20Image%202026-07-17%20at%205.33.35%20PM.jpeg";

const DIRECTIONS_URL =
  "https://www.google.com/maps/search/?api=1&query=" +
  encodeURIComponent(ADDRESS);

export default function VisitBoutique() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="visit" ref={ref} data-testid="visit-boutique" className="bg-cream py-24 md:py-36 overflow-hidden">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Storefront image */}
        <Reveal className="order-2 lg:order-1">
          <div className="relative aspect-[4/5] max-h-[680px] w-full overflow-hidden border border-charcoal/15">
            <motion.img
              style={{ y: imgY }}
              src={STOREFRONT_IMG}
              alt="TOGA boutique storefront, Vashi"
              className="absolute inset-0 w-full h-[112%] object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-charcoal/85 backdrop-blur-sm px-6 py-4 flex items-center justify-between">
              <span className="text-cream text-xs uppercase tracking-[0.2em] font-bold">टोगा TOGA · Sector 17</span>
              <span className="text-toga text-xs uppercase tracking-[0.2em] font-bold">Vashi</span>
            </div>
          </div>
        </Reveal>

        {/* Content */}
        <div className="order-1 lg:order-2">
          <Reveal>
            <Overline>Visit Us</Overline>
            <h2 className="mt-5 font-display font-black uppercase tracking-normal text-charcoal text-4xl md:text-6xl leading-[0.95]">
              Come see us in <span className="text-toga">Vashi.</span>
            </h2>
            <p className="mt-6 text-lg font-light text-charcoal/70 max-w-md leading-relaxed">
              Come to our store and feel the fabrics for yourself. Walk in for a fitting, browse the collection, or sit with our tailors to plan a custom piece.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 space-y-5 border-t border-charcoal/15 pt-8">
              <div className="flex items-start gap-4">
                <MapPin className="text-toga mt-0.5 shrink-0" size={20} />
                <p className="font-light text-charcoal/80 leading-relaxed">{ADDRESS}</p>
              </div>
              <div className="flex items-center gap-4">
                <Clock className="text-toga shrink-0" size={20} />
                <p className="font-light text-charcoal/80">Open Daily · 10:00 AM – 10:00 PM</p>
              </div>
              <a href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`} className="flex items-center gap-4 hover:text-toga transition-colors">
                <Phone className="text-toga shrink-0" size={20} />
                <p className="font-light text-charcoal/80">{PHONE_DISPLAY}</p>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="visit-directions-btn"
                className="inline-flex items-center gap-3 bg-charcoal text-cream px-8 py-4 text-xs uppercase tracking-[0.18em] font-bold hover:bg-toga transition-colors duration-300"
              >
                <Navigation size={16} /> Get Directions
              </a>
              <a
                href={whatsappLink("Hello TOGA, I'd like to visit the boutique. Please share your location.")}
                target="_blank"
                rel="noreferrer"
                data-testid="visit-whatsapp-btn"
                className="inline-flex items-center gap-3 border border-charcoal text-charcoal px-8 py-4 text-xs uppercase tracking-[0.18em] font-bold hover:bg-charcoal hover:text-cream transition-colors duration-300"
              >
                <MessageCircle size={16} /> Plan a Visit
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
